import { useEffect, useState } from 'react';
import { useCompanyInfo } from '@/hooks/useCompanyInfo';
import {
  CompanyInfo,
  CompanyLocation,
  createEmptyLocation,
} from '@/lib/companyInfo';

interface CompanyInfoManagerProps {
  canEdit: boolean;
}

const inputClass =
  'w-full px-4 py-2 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors disabled:bg-stone-50 disabled:text-stone-400';

export default function CompanyInfoManager({ canEdit }: CompanyInfoManagerProps) {
  const { info, loading, error, saving, save, reload } = useCompanyInfo();
  const [draft, setDraft] = useState<CompanyInfo>(info);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setDraft(info);
  }, [info]);

  const updateField = (field: keyof CompanyInfo, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updateLocation = (id: string, field: keyof CompanyLocation, value: string) => {
    setDraft((prev) => ({
      ...prev,
      locations: prev.locations.map((loc) =>
        loc.id === id ? { ...loc, [field]: value } : loc
      ),
    }));
  };

  const addBranch = () => {
    setDraft((prev) => ({
      ...prev,
      locations: [...prev.locations, createEmptyLocation('branch')],
    }));
  };

  const removeBranch = (id: string) => {
    setDraft((prev) => ({
      ...prev,
      locations: prev.locations.filter((loc) => loc.id !== id),
    }));
  };

  const hq = draft.locations.find((l) => l.type === 'hq') || createEmptyLocation('hq');
  const branches = draft.locations.filter((l) => l.type === 'branch');

  const handleSave = async () => {
    setMessage(null);
    if (!draft.companyName.trim()) {
      setMessage('회사명을 입력해 주세요.');
      return;
    }
    try {
      await save(draft);
      setMessage('회사정보가 저장됐어요.');
      setTimeout(() => setMessage(null), 2500);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : '저장에 실패했어요.');
    }
  };

  return (
    <section>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-stone-900">회사정보</h2>
        <p className="text-sm text-stone-500 mt-1">
          사이트 곳곳(상단 바, 연락처 페이지 등)에 표시되는 회사명·연락처·본사/지사 정보예요. 수정 후 저장 버튼을 눌러야 반영돼요.
        </p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-5 py-4 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-600 text-lg" />
          <p className="text-sm text-red-700 flex-1">회사정보를 불러오지 못했어요: {error}</p>
          <button
            type="button"
            onClick={() => reload()}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-red-300 text-red-700 hover:bg-red-100 transition-colors whitespace-nowrap"
          >
            다시 시도
          </button>
        </div>
      )}

      {message && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-xl px-5 py-3 flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-green-600" />
          <span className="text-sm text-green-700">{message}</span>
        </div>
      )}

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-stone-400">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
            <span className="text-sm">불러오는 중...</span>
          </div>
        ) : (
          <div className="p-5 md:p-6 space-y-8">
            {/* Basic info */}
            <div>
              <h3 className="text-sm font-semibold text-stone-700 mb-3">기본 정보</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1.5">
                    회사명
                  </label>
                  <input
                    type="text"
                    value={draft.companyName}
                    disabled={!canEdit || saving}
                    onChange={(e) => updateField('companyName', e.target.value)}
                    placeholder="GBCOSPACK"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1.5">
                    대표 이메일
                  </label>
                  <input
                    type="email"
                    value={draft.email}
                    disabled={!canEdit || saving}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="trade@gbcospack.com"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1.5">
                    대표 전화 (한국)
                  </label>
                  <input
                    type="text"
                    value={draft.phoneKr}
                    disabled={!canEdit || saving}
                    onChange={(e) => updateField('phoneKr', e.target.value)}
                    placeholder="+82-2-1234-5678"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1.5">
                    대표 전화 (중국)
                  </label>
                  <input
                    type="text"
                    value={draft.phoneCn}
                    disabled={!canEdit || saving}
                    onChange={(e) => updateField('phoneCn', e.target.value)}
                    placeholder="+86 21 0000 0000"
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            {/* Headquarters */}
            <div>
              <h3 className="text-sm font-semibold text-stone-700 mb-3">본사</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-stone-50 rounded-xl p-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1.5">
                    본사 이름
                  </label>
                  <input
                    type="text"
                    value={hq.name}
                    disabled={!canEdit || saving}
                    onChange={(e) => updateLocation(hq.id, 'name', e.target.value)}
                    placeholder="서울 — 성수동"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1.5">
                    본사 주소
                  </label>
                  <input
                    type="text"
                    value={hq.address}
                    disabled={!canEdit || saving}
                    onChange={(e) => updateLocation(hq.id, 'address', e.target.value)}
                    placeholder="대한민국 서울특별시 성동구 성수동"
                    className={inputClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-stone-500 mb-1.5">
                    본사 설명
                  </label>
                  <input
                    type="text"
                    value={hq.note}
                    disabled={!canEdit || saving}
                    onChange={(e) => updateLocation(hq.id, 'note', e.target.value)}
                    placeholder="글로벌 소싱 & 고객 성공"
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            {/* Branches */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-stone-700">
                  지사{' '}
                  <span className="text-stone-400 font-normal">({branches.length})</span>
                </h3>
                <button
                  type="button"
                  disabled={!canEdit || saving}
                  onClick={addBranch}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-add-line" />
                  지사 추가
                </button>
              </div>

              {branches.length === 0 ? (
                <div className="bg-stone-50 rounded-xl px-5 py-6 text-center text-sm text-stone-400">
                  등록된 지사가 없어요. '지사 추가' 버튼으로 추가해 주세요.
                </div>
              ) : (
                <div className="space-y-4">
                  {branches.map((branch, idx) => (
                    <div key={branch.id} className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-stone-500">
                          지사 {idx + 1}
                        </span>
                        <button
                          type="button"
                          disabled={!canEdit || saving}
                          onClick={() => removeBranch(branch.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <i className="ri-delete-bin-line" />
                          삭제
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-stone-500 mb-1.5">
                            지사 이름
                          </label>
                          <input
                            type="text"
                            value={branch.name}
                            disabled={!canEdit || saving}
                            onChange={(e) => updateLocation(branch.id, 'name', e.target.value)}
                            placeholder="상하이 — 민항"
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-stone-500 mb-1.5">
                            지사 주소
                          </label>
                          <input
                            type="text"
                            value={branch.address}
                            disabled={!canEdit || saving}
                            onChange={(e) => updateLocation(branch.id, 'address', e.target.value)}
                            placeholder="중국 상하이시 민항구"
                            className={inputClass}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs font-medium text-stone-500 mb-1.5">
                            지사 설명
                          </label>
                          <input
                            type="text"
                            value={branch.note}
                            disabled={!canEdit || saving}
                            onChange={(e) => updateLocation(branch.id, 'note', e.target.value)}
                            placeholder="동중국 소싱 & 품질관리"
                            className={inputClass}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Save */}
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-stone-100">
              <button
                type="button"
                disabled={!canEdit || saving}
                onClick={handleSave}
                className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                {saving ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-save-line" />
                )}
                저장
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}